import {
  useState,
  useRef,
  useEffect,
  useEffectEvent,
  type ReactNode,
  type KeyboardEvent,
} from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface SearchableSelectProps<T> {
  /** Options to show. The caller fetches these in response to `onSearchChange`. */
  options: T[];
  /** Stable identity for an option - used as the React key and for selection. */
  getOptionKey: (option: T) => string;
  /** Row content for an option. Keep it to text/badges; the row owns its layout. */
  renderOption: (option: T) => ReactNode;
  getOptionGroup?: (option: T) => string;
  onSelect: (option: T) => void;
  /**
   * Debounced search term, emitted as the user types and reset to '' on close.
   * The component owns the raw input and the debounce; the caller only decides
   * what to fetch.
   */
  onSearchChange?: (search: string) => void;
  /**
   * Open-state changes, so the caller can gate its query (`enabled: open`)
   * instead of fetching in the background.
   */
  onOpenChange?: (open: boolean) => void;
  /** Key of the selected option; drives the tick mark. */
  selectedKey?: string;
  /** Trigger content when something is selected. Falls back to `placeholder`. */
  triggerLabel?: ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Shown when the search returned nothing. */
  emptyMessage?: string;
  /** Shown before the user has typed anything. */
  promptMessage?: string;
  isLoading?: boolean;
  /** Renders a "Clear selection" row at the top while something is selected. */
  onClear?: () => void;
  clearLabel?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** Debounce for `onSearchChange`, in ms. */
  debounceMs?: number;
}

/**
 * Generic searchable dropdown over a caller-supplied option list.
 *
 * Owns the interaction concerns that are identical for every entity picker -
 * open state, the search input and its debounce, keyboard navigation,
 * click-outside, and scroll-into-view - and leaves fetching and rendering to
 * the caller. Works for both local and server-backed option lists: emit the
 * search term, hand back `options` and `isLoading`.
 */
export function SearchableSelect<T>({
  options,
  getOptionKey,
  renderOption,
  getOptionGroup,
  onSelect,
  onSearchChange,
  onOpenChange,
  selectedKey,
  triggerLabel,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found.',
  promptMessage = 'Type to search.',
  isLoading = false,
  onClear,
  clearLabel = 'Clear selection',
  disabled = false,
  id,
  className,
  debounceMs = 250,
}: SearchableSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionsRef = useRef<Array<HTMLDivElement | null>>([]);

  const showClear = !!onClear && !!selectedKey;
  // The clear row occupies index 0, so option N sits at N + clearOffset.
  const clearOffset = showClear ? 1 : 0;
  const itemCount = options.length + clearOffset;

  const setOpenState = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const closeDropdown = ({ refocus = true }: { refocus?: boolean } = {}) => {
    setOpenState(false);
    setSearch('');
    setHighlightedIndex(0);
    if (refocus) buttonRef.current?.focus();
  };

  // Closing from a click outside is non-reactive: it must not re-subscribe the
  // listener whenever a prop changes, and refocusing the trigger would steal
  // focus from whatever the user just clicked.
  const closeFromClickOutside = useEffectEvent(() =>
    closeDropdown({ refocus: false })
  );

  // Debounce so a keystroke burst triggers one fetch, not one per letter.
  useEffect(() => {
    const timer = setTimeout(() => onSearchChange?.(search), debounceMs);
    return () => clearTimeout(timer);
  }, [search, debounceMs, onSearchChange]);

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        closeFromClickOutside();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    const node = optionsRef.current[highlightedIndex];
    if (node) node.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  const handleSelect = (option: T) => {
    onSelect(option);
    closeDropdown();
  };

  const handleClear = () => {
    onClear?.();
    closeDropdown();
  };

  // Enter activates whichever row is highlighted, clear row included.
  const activateHighlighted = () => {
    if (showClear && highlightedIndex === 0) {
      handleClear();
      return;
    }
    const option = options[highlightedIndex - clearOffset];
    if (option) handleSelect(option);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prev) =>
          itemCount === 0 ? -1 : (prev + 1) % itemCount
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prev) =>
          itemCount === 0 ? -1 : (prev - 1 + itemCount) % itemCount
        );
        break;
      case 'Enter':
        event.preventDefault();
        activateHighlighted();
        break;
      case 'Escape':
        event.preventDefault();
        closeDropdown();
        break;
    }
  };

  const noOptionsMessage = search ? emptyMessage : promptMessage;

  return (
    <div className="relative">
      <Button
        ref={buttonRef}
        id={id}
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        disabled={disabled}
        className={cn(
          'w-full justify-between font-normal',
          !triggerLabel && 'text-muted-foreground',
          className
        )}
        onClick={() => {
          if (open) {
            closeDropdown();
          } else {
            setHighlightedIndex(0);
            setOpenState(true);
          }
        }}
      >
        <span className="truncate">{triggerLabel || placeholder}</span>
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full rounded-md border bg-popover p-0 text-popover-foreground shadow-md outline-hidden"
        >
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              className="flex h-11 w-full border-0 bg-transparent py-3 text-sm outline-hidden placeholder:text-muted-foreground focus:outline-hidden focus:ring-0"
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setHighlightedIndex(0);
              }}
              onKeyDown={handleSearchKeyDown}
              autoFocus
            />
          </div>

          <div role="listbox" className="max-h-[300px] overflow-auto p-1">
            {showClear && (
              <div
                ref={(node) => {
                  optionsRef.current[0] = node;
                }}
                role="option"
                aria-selected={false}
                onMouseEnter={() => setHighlightedIndex(0)}
                onClick={handleClear}
                className={cn(
                  'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm text-muted-foreground outline-hidden transition-colors hover:bg-accent hover:text-accent-foreground',
                  highlightedIndex === 0 && 'bg-accent text-accent-foreground'
                )}
              >
                <X className="mr-2 h-4 w-4 shrink-0" />
                {clearLabel}
              </div>
            )}

            {isLoading || options.length === 0 ? (
              <div className="px-2 py-1.5 text-sm text-muted-foreground">
                {isLoading ? 'Searching...' : noOptionsMessage}
              </div>
            ) : (
              options.map((option, index) => {
                const key = getOptionKey(option);
                const flatIndex = index + clearOffset;
                return (
                  <SearchableSelectOption
                    key={key}
                    ref={(node) => {
                      optionsRef.current[flatIndex] = node;
                    }}
                    option={option}
                    groupHeader={groupHeaderFor(options, index, getOptionGroup)}
                    isFirstOption={index === 0}
                    isSelected={selectedKey === key}
                    isHighlighted={highlightedIndex === flatIndex}
                    renderOption={renderOption}
                    onHighlight={() => setHighlightedIndex(flatIndex)}
                    onSelect={() => handleSelect(option)}
                  />
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function groupHeaderFor<T>(
  options: T[],
  index: number,
  getOptionGroup?: (option: T) => string
): string | undefined {
  const group = getOptionGroup?.(options[index]);
  if (!group) return undefined;
  if (index > 0 && getOptionGroup?.(options[index - 1]) === group)
    return undefined;
  return group;
}

interface SearchableSelectOptionProps<T> {
  option: T;
  groupHeader?: string;
  isFirstOption: boolean;
  isSelected: boolean;
  isHighlighted: boolean;
  renderOption: (option: T) => ReactNode;
  onHighlight: () => void;
  onSelect: () => void;
  ref: (node: HTMLDivElement | null) => void;
}

function SearchableSelectOption<T>({
  option,
  groupHeader,
  isFirstOption,
  isSelected,
  isHighlighted,
  renderOption,
  onHighlight,
  onSelect,
  ref,
}: SearchableSelectOptionProps<T>) {
  return (
    <>
      {groupHeader && (
        <div
          role="presentation"
          className={cn(
            'px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground',
            !isFirstOption && 'mt-1 border-t pt-2'
          )}
        >
          {groupHeader}
        </div>
      )}
      <div
        ref={ref}
        role="option"
        aria-selected={isSelected}
        onMouseEnter={onHighlight}
        onClick={onSelect}
        className={cn(
          'relative flex cursor-pointer select-none items-start rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors hover:bg-accent hover:text-accent-foreground',
          isHighlighted && 'bg-accent text-accent-foreground'
        )}
      >
        <Check
          className={cn(
            'mr-2 mt-0.5 h-4 w-4 shrink-0',
            isSelected ? 'opacity-100' : 'opacity-0'
          )}
        />
        <div className="min-w-0 flex-1">{renderOption(option)}</div>
      </div>
    </>
  );
}
